import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertCircle, Loader2, Trash2, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RoleBadge } from "./RoleBadge";
import { useTeamRole } from "@/hooks/useTeamRole";
import {
  ROLE_META,
  TEAM_ROLES,
  TeamRole,
  canManageMembers,
  normalizeRole,
} from "@/lib/team-roles";

interface Team {
  id: string;
  name: string;
  owner_id?: string;
}

interface TeamMember {
  id: string;
  user_id: string;
  team_id: string;
  role: TeamRole;
  user_email?: string;
}

interface ManageTeamDialogProps {
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamUpdated: () => void;
}

const initials = (email?: string) => (email || "?").slice(0, 2).toUpperCase();

export const ManageTeamDialog = ({ team, open, onOpenChange, onTeamUpdated }: ManageTeamDialogProps) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<TeamRole>("viewer");
  const [isAddingMember, setIsAddingMember] = useState(false);

  const { role, userId } = useTeamRole(open ? team?.id : null, team?.owner_id);
  const canManage = canManageMembers(role);

  const fetchMembers = useCallback(async () => {
    if (!team) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data: teamMembers, error: membersError } = await supabase
        .from("team_members")
        .select("*")
        .eq("team_id", team.id);

      if (membersError) throw new Error(membersError.message);

      const enriched = await Promise.all(
        (teamMembers || []).map(async (member) => {
          try {
            const { data } = await supabase.functions.invoke("get-user-email", {
              body: { userId: member.user_id },
            });
            return { ...member, role: normalizeRole(member.role), user_email: data?.email || "Unknown user" };
          } catch {
            return { ...member, role: normalizeRole(member.role), user_email: "Unknown user" };
          }
        })
      );

      setMembers(enriched as TeamMember[]);
    } catch (err: any) {
      setError(err.message);
      toast.error("Failed to load team members");
    } finally {
      setIsLoading(false);
    }
  }, [team]);

  useEffect(() => {
    if (open && team) fetchMembers();
  }, [open, team, fetchMembers]);

  const handleRoleChange = async (memberId: string, newRole: TeamRole) => {
    const { error: updateError } = await supabase
      .from("team_members")
      .update({ role: newRole })
      .eq("id", memberId)
      .select("id");

    if (updateError) {
      toast.error(updateError.message || "Failed to update role");
      return;
    }
    toast.success("Role updated");
    fetchMembers();
  };

  const handleRemoveMember = async (memberId: string) => {
    const { error: deleteError } = await supabase
      .from("team_members")
      .delete()
      .eq("id", memberId)
      .select("id");

    if (deleteError) {
      toast.error(deleteError.message || "Failed to remove member");
      return;
    }
    toast.success("Team member removed");
    fetchMembers();
    onTeamUpdated();
  };

  const handleAddMember = async () => {
    if (!team || !newMemberEmail.trim()) return;
    setIsAddingMember(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("invite-team-member", {
        body: { teamId: team.id, email: newMemberEmail.trim(), role: newMemberRole },
      });
      if (data?.error) throw new Error(data.error);
      if (fnError) throw new Error(fnError.message);

      const who = newMemberEmail.trim();
      if (data?.pending) {
        toast.success(
          data.emailSent
            ? `Invitation sent to ${who}. They join as ${ROLE_META[newMemberRole].label} once they sign up.`
            : `${who} is invited as ${ROLE_META[newMemberRole].label}, but the invitation email could not be sent.`,
        );
      } else {
        toast.success(
          data?.emailSent === false
            ? `${who} added as ${ROLE_META[newMemberRole].label} (notification email could not be sent).`
            : `${who} added as ${ROLE_META[newMemberRole].label}`,
        );
      }
      setNewMemberEmail("");
      setNewMemberRole("viewer");
      fetchMembers();
      onTeamUpdated();
    } catch (err: any) {
      toast.error(err.message || "Failed to invite team member");
    } finally {
      setIsAddingMember(false);
    }
  };

  const disabledReason = "Only the team owner and admins can manage members";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {team?.name}
            {role && <RoleBadge role={role} />}
          </DialogTitle>
          <DialogDescription>Manage who belongs to this team and what they can do.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="members">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="roles">Roles</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="space-y-4 pt-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      placeholder="Add member by email"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                      disabled={!canManage}
                      className="flex-1"
                    />
                    <Select
                      value={newMemberRole}
                      onValueChange={(v) => setNewMemberRole(v as TeamRole)}
                      disabled={!canManage}
                    >
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_META[r].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddMember}
                      disabled={!canManage || isAddingMember || !newMemberEmail.trim()}
                    >
                      {isAddingMember ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <UserPlus className="mr-2 h-4 w-4" />
                      )}
                      Invite
                    </Button>
                  </div>
                </TooltipTrigger>
                {!canManage && <TooltipContent>{disabledReason}</TooltipContent>}
              </Tooltip>
            </TooltipProvider>

            <div className="space-y-2">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)
              ) : members.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No members yet — invite a teammate by email above.
                </p>
              ) : (
                members.map((member) => {
                  const isTeamOwner = member.user_id === team?.owner_id;
                  const editable = canManage && !isTeamOwner;
                  return (
                    <div
                      key={member.id}
                      className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3"
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs">{initials(member.user_email)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {member.user_email}
                          {member.user_id === userId && (
                            <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {ROLE_META[isTeamOwner ? "owner" : member.role].description}
                        </p>
                      </div>
                      {isTeamOwner ? (
                        <RoleBadge role="owner" />
                      ) : editable ? (
                        <Select
                          value={member.role}
                          onValueChange={(v) => handleRoleChange(member.id, v as TeamRole)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TEAM_ROLES.map((r) => (
                              <SelectItem key={r} value={r}>
                                {ROLE_META[r].label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <RoleBadge role={member.role} />
                      )}
                      {editable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${member.user_email}`}
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="roles" className="space-y-3 pt-4">
            {(["owner", "admin", "editor", "viewer"] as const).map((r) => (
              <div key={r} className="flex items-start gap-3 rounded-lg border p-3">
                <RoleBadge role={r} />
                <p className="text-sm text-muted-foreground">{ROLE_META[r].description}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ManageTeamDialog;
